import React, { useState } from "react";
import { api, formatError } from "@/lib/api";
import { toast } from "@/lib/toast";
import { Ban, X, ShieldOff } from "lucide-react";
import { BLOCK_REASONS } from "@/lib/constants";

export function BlockUserButton({ username, blocked, onChanged, className = "" }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!reason) { toast.error("Please choose a reason."); return; }
    if (reason === "other" && !note.trim()) { toast.error("Please add a short reason."); return; }
    setBusy(true);
    try {
      await api.post(`/users/${username}/block`, { reason, note });
      toast.success(`@${username} blocked. They can't contact you and you won't see each other's listings.`);
      setOpen(false); setReason(""); setNote("");
      onChanged?.(true);
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const unblock = async () => {
    setBusy(true);
    try {
      await api.delete(`/users/${username}/block`);
      toast.success(`@${username} unblocked.`);
      onChanged?.(false);
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (blocked) {
    return (
      <button onClick={unblock} disabled={busy} data-testid="unblock-btn"
        className={className || "inline-flex items-center gap-1.5 border border-blaze/50 text-blaze hover:bg-blaze/10 font-mono uppercase text-[11px] px-3 py-2 transition-colors"}>
        <ShieldOff className="w-3.5 h-3.5" /> Unblock
      </button>
    );
  }

  return (
    <>
      <button onClick={() => setOpen(true)} data-testid="block-btn"
        className={className || "inline-flex items-center gap-1.5 border border-sniper hover:border-blaze text-secondary-foreground hover:text-blaze font-mono uppercase text-[11px] px-3 py-2 transition-colors"}>
        <Ban className="w-3.5 h-3.5" /> Block
      </button>
      {open && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/70 p-4" data-testid="block-modal" onClick={() => !busy && setOpen(false)}>
          <div className="w-full max-w-md border border-sniper bg-surface p-5" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="font-heading text-xl uppercase tracking-tight text-foreground flex items-center gap-2"><Ban className="w-4 h-4 text-blaze" /> Block @{username}</h3>
              <button onClick={() => setOpen(false)} data-testid="block-close" className="text-muted-foreground hover:text-foreground"><X className="w-5 h-5" /></button>
            </div>
            <p className="text-xs font-body text-secondary-foreground mt-2">They won't be able to message or contact you, and you won't see each other's listings. They aren't told they've been blocked.</p>
            <label className="block font-mono text-[10px] uppercase text-muted-foreground mt-4 mb-1">Reason (required)</label>
            <select value={reason} onChange={(e) => setReason(e.target.value)} data-testid="block-reason"
              className="w-full bg-input border border-sniper focus:border-olive outline-none px-3 py-2 text-foreground font-mono text-sm transition-colors">
              <option value="" disabled>Choose a reason…</option>
              {BLOCK_REASONS.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
            </select>
            <label className="block font-mono text-[10px] uppercase text-muted-foreground mt-3 mb-1">
              {reason === "other" ? "Tell us more (required)" : "Details (optional)"}
            </label>
            <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} data-testid="block-note"
              placeholder={reason === "other" ? "What happened?" : "Optional context for moderators"}
              className="w-full bg-input border border-sniper focus:border-olive outline-none px-3 py-2 text-foreground font-body text-sm transition-colors" />
            <button onClick={submit} disabled={busy} data-testid="block-submit"
              className="w-full mt-4 bg-blaze hover:bg-blaze/90 disabled:opacity-60 text-white font-heading font-bold uppercase py-2.5 transition-colors">
              {busy ? "Blocking…" : "Block user"}
            </button>
          </div>
        </div>
      )}
    </>
  );
}
